import React, { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  InputAdornment,
  TextField,
} from '@mui/material';

import Autocomplete from '@mui/material/Autocomplete';

import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import BuildIcon from '@mui/icons-material/Build';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

import useSWR from 'swr';

import cars from '../constants/cars';

import axiosInstance from '../lib/axiosInstance';

import ServiceIcon from './ServiceIcon';


function PersonalisedEstimate({ initialMake, initialModel, initialService }) {

  const [make, setMake] = useState(initialMake || '');
  const [model, setModel] = useState(initialModel || '');
  const [models, setModels] = useState([]);
  const [service, setService] = useState({});
  const [errorMessage, setErrorMessage] = useState();

  const router = useRouter();

  const modelInputRef = useRef();

  const { data, error } = useSWR('/content/services/?page_size=1000');
  const services = data?.results;
  const orderedServices = services?.length ? services.sort((a, b) => {
    if (a.category > b.category) {
      return 1;
    } else {
      return -1;
    }
  }) : [];

  useEffect(() => {
    if (services?.length && initialService) {
      const selectedService = services.find(service => service.name === initialService);
      setService(selectedService);
    }
  }, [services, initialService])

  useEffect(() => {
    if (!make) {
      return
    }
    const fetchData = async () => {
      if (errorMessage) {
        setErrorMessage('');
      }
      try {
        setModels([]);
        const result = await axiosInstance.get(`/api/v2/car-models/?make=${make}`);
        setModels(result.data);
        // modelInputRef.current.focus();
      } catch (error) {
        console.log('error', error);
      }
    };

    fetchData();
  }, [make, errorMessage]);

  const getQuotes = () => {
    const requestProgress = {
      maxStep: 1,
      car: {
        make,
        model,
        year: '',
        engine: '',
        vin: '',
      },
      carSearchData: {
        makes: cars,
        models,
      },
      service,
      services,
    };

    localStorage.removeItem('requestProgress');
    localStorage.removeItem('hooked');

    localStorage.setItem('requestProgress', JSON.stringify(requestProgress));
    localStorage.setItem('personalQuote', 1);
    // set localstorage
    router.push('/request-quotes/?step=0');
  }

  return (
    <Box
      sx={{
        pt: 3,
        pb: 2,
        px: 2,
        color: 'white',
        bgcolor: '#1B4251'
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          mb: 4
        }}
      >
        <Image
          src="/icons/personalized-quote.svg"
          width={96}
          height={110}
          alt="Get cost estimates"
        />
        <Box maxWidth={200}>
          <Box fontSize="h5.fontSize" fontWeight="500">
            Looking for a more personalised quote?
          </Box>
          <Box fontSize="body2.fontSize" fontWeight="300">
            Receive estimates from local mechanics near you!
          </Box>
        </Box>
      </Box>

      <Box mb={2} display="flex">
        <Autocomplete
          options={cars}
          value={make}
          size="small"
          fullWidth
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              name="make"
              size="small"
              label="Make"
              placeholder="Select"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <DirectionsCarIcon color="secondary" fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  color: 'white',
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white'
                  }
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: 'primary.light',
                },
                '& label': {
                  color: 'white',
                },
                '& label.Mui-focused': {
                  color: 'white',
                },
                '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                  color: 'white',
                }
              }}
            />
          )}
          onChange={(event, value) => {
            setMake(value);
            if (model) {
              setModel('')
            }
          }}
        />

        <Box px={0.5} />

        <Autocomplete
          options={models}
          value={model}
          fullWidth
          disableClearable
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
          renderInput={(params) => (
            <TextField
              {...params}
              name="model"
              size="small"
              label="Model"
              placeholder="Select"
              variant="outlined"
              inputRef={modelInputRef}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <AirportShuttleIcon color="secondary" fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  color: 'white',
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white'
                  }
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: 'primary.light',
                },
                '& label': {
                  color: 'white',
                },
                '& label.Mui-focused': {
                  color: 'white',
                },
                '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                  color: 'white',
                }
              }}
            />
          )}
          onChange={(event, value) => {
            setModel(value);
          }}
        />
      </Box>

      <Box my={2}>
        <Autocomplete
          disableClearable
          value={service?.name || ''}
          options={orderedServices || []}
          onChange={(event, value) => {
            setService(value);
          }}
          groupBy={option => option.category}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          isOptionEqualToValue={(option, value) => (option.name == value)}
          renderOption={(props, option) => (
            <Box display="flex" alignItems="center" {...props}>
              <Box height={24} mr={1}>
                {option.category && <ServiceIcon service={option.category} />}
              </Box>
              {option.name}
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Service"
              placeholder="Type to search, e.g. brakes or clutch"
              size="small"
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <BuildIcon color="secondary" fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  color: 'white',
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white'
                  }
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: 'primary.light',
                },
                '& label': {
                  color: 'white',
                },
                '& label.Mui-focused': {
                  color: 'white',
                },
                '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                  color: 'white',
                }
              }}
            />
          )}
        />
      </Box>

      {make && model && service &&
        <Button
          size="large"
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => getQuotes()}
          sx={{ mt: 2 }}
        >Send Me Quotes</Button>
      }
    </Box>
  );
}

export default PersonalisedEstimate;
